"use client"

import { useState, useEffect } from "react"
import { Crown, X } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useFeatureFlag } from "@/lib/feature-flags"
import { useAdStore } from "@/lib/ad-store"
import { useSkipAdsModalActive } from "@/lib/skip-ads"
import { useTranslations } from "next-intl"
import { usePatreonMode } from "@/lib/use-patreon-mode"

interface MembershipPromoStripProps {
  isModalOpen: boolean
  onFounderClick: () => void
  founderSlotsRemaining: number
  founderSoldOut: boolean
  className?: string
}

export function MembershipPromoStrip({
  isModalOpen,
  onFounderClick,
  founderSlotsRemaining,
  founderSoldOut,
  className,
}: MembershipPromoStripProps) {
  const t = useTranslations("membershipPromoStrip")
  const { enabled: patreonMode } = usePatreonMode()
  const [dismissed, setDismissed] = useState(false)
  const { enabled: stripEnabled, loading } = useFeatureFlag("membership_strip_enabled")
  const { isPremiumUser } = useAdStore()
  // SkipAdsModal is mounted separately in app/layout.tsx (via SkipAdsButton),
  // so its open state doesn't reach this component through the isModalOpen
  // prop (that one only tracks this page's own post-detail modal). Both are
  // fixed to the same bottom edge at the same z-40 — without this, the strip
  // rendered on top of / overlapped SkipAdsModal's bottom content while it
  // was open.
  const skipAdsModalActive = useSkipAdsModalActive()

  const visible = !loading && stripEnabled && !isPremiumUser && !isModalOpen && !skipAdsModalActive && !dismissed

  // Tell floating buttons how tall the strip is so they can shift up
  useEffect(() => {
    document.documentElement.style.setProperty("--promo-strip-h", visible ? "44px" : "0px")
    return () => document.documentElement.style.setProperty("--promo-strip-h", "0px")
  }, [visible])

  if (!visible) return null

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-40",
        "h-[44px] flex items-center justify-between px-2 sm:px-4",
        "bg-background/95 backdrop-blur border-t border-border/50",
        "shadow-[0_-4px_20px_rgba(111,77,235,0.15)]",
        className
      )}
    >
      {/* Left: icon + text */}
      <button
        onClick={onFounderClick}
        className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1 hover:opacity-80 transition-opacity text-left"
      >
        <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <div className="min-w-0 truncate">
          {!founderSoldOut ? (
            <>
              <span className="font-semibold text-xs text-foreground">{t("foundingMember")}</span>
              <span className="text-muted-foreground text-xs ml-1.5 hidden sm:inline">
                {patreonMode ? t("patreonFounderSlots", { founderSlotsRemaining }) : t("founderSlotsRemainingOf250SlotsLeft79", { founderSlotsRemaining })}
              </span>
              <span className="text-muted-foreground text-xs ml-1.5 sm:hidden">
                {t("founderSlotsRemainingLeft", { founderSlotsRemaining })}
              </span>
            </>
          ) : (
            <>
              <span className="font-semibold text-xs text-foreground">{t("upgradeYourExperience")}</span>
              <span className="text-muted-foreground text-xs ml-1.5 hidden sm:inline">
                {t("noAdsUnlimitedDownloadsPrioritySupport")}
              </span>
            </>
          )}
        </div>
      </button>

      {/* Right: CTA + dismiss */}
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2 sm:ml-3">
        <Button
          size="sm"
          onClick={onFounderClick}
          className="h-6 sm:h-7 text-[11px] sm:text-xs px-2 sm:px-3 bg-gradient-to-r from-amber-500 to-purple-600 hover:from-amber-400 hover:to-purple-500 text-white border-0"
        >
          {founderSoldOut ? t("upgrade") : t("claimSpot")}
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded shrink-0"
          aria-label={t("dismiss")}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
